import React from 'react';
import { useTranslation } from "react-i18next";
import styles from './ExperienceGraph.module.scss';
import { CompanyPageBid } from '../../../../../../hooks/slices/companySlice'; // Adjusted
import { DataStackedAttributes } from '../../../../../../components/graphs/scatterGraph'; // Type might be generic or from BarGraphStacked?
import { BarGraphStacked } from '../../../../../../components/graphs'; // Adjusted
import moment from 'moment';
import { DateUtil } from '../../../../../../utils/date'; // Adjusted
import {
    calculateMetricByYear,
    calculateYoYChange,
    getSimpleYoYSubtitleProps,
    reduceBidsByYear,
    SimpleYoYSubtitleResult // Ensure exported
} from '../../../info/infoUtils'; // Adjusted relative path

interface ExperienceGraphProps {
    bids: CompanyPageBid[];
}

export const ExperienceGraph: React.FC<ExperienceGraphProps> = (props) => {
    const { t } = useTranslation();
    const bids: CompanyPageBid[] = props.bids ?? [];

    // Filter for winning bids only
    const winningBids = bids.filter(bid => bid.bidRole === "WINNER");

    // --- Calculate based on UNIQUE Tenders Won --- 
    const uniqueWinningTenderUuids = Array.from(new Set(winningBids.map(bid => bid.tenderUuid)));
    const totalUniqueWins = uniqueWinningTenderUuids.length;

    // Calculate year-over-year change in *unique tenders won* count
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;
    const relevantYears = [lastYear, currentYear];

    const yearlyWinningBids = reduceBidsByYear(winningBids);

    // Calculate unique wins count per year
    const yearlyUniqueWinsCount = calculateMetricByYear<number>(
        yearlyWinningBids,
        relevantYears,
        (bidsInYear: CompanyPageBid[]) => new Set(bidsInYear.map(bid => bid.tenderUuid)).size,
        0 // Default count is 0
    );

    const currentYearCount = yearlyUniqueWinsCount[currentYear.toString()];
    const lastYearCount = yearlyUniqueWinsCount[lastYear.toString()];

    // Use new YoY helper for percentage change
    const yearOverYearChange = calculateYoYChange(currentYearCount, lastYearCount, 'percentageChange');
    
    // Show subtitle if change could be calculated OR if either year had data (consistent with previous logic)
    const hasSufficientData = yearOverYearChange !== null || currentYearCount > 0 || lastYearCount > 0; 

    const subtitleProps: SimpleYoYSubtitleResult | null = (yearOverYearChange !== null) ? getSimpleYoYSubtitleProps({
        yearOverYearChange: yearOverYearChange, // Change could be 0, that's fine
        t: t,
        increaseKey: "companyPage.experience_tenders_won_increase",
        decreaseKey: "companyPage.experience_tenders_won_decrease",
        noChangeKey: "companyPage.experience_tenders_won_no_change", 
    }) : null;

    const changeText = (yearOverYearChange !== null) ? Math.abs(yearOverYearChange).toFixed(1) : '';

    // Helper function to render the subtitle content (adapted)
    const renderSubtitleContent = () => {
        if (!hasSufficientData) {
            return <div className={styles.subtitle}>{t("companyPage.experience_title_2_tenders_won", "vs last year")}</div>; 
        }
        // If change could be calculated (even if 0), show it
        if (subtitleProps && yearOverYearChange !== null) { 
            return (
                <div className={`${styles.subtitle}`}> 
                    <span className={`${styles["change-indicator"]} ${styles[`change-indicator-${subtitleProps.colorSuffix}`]}`}> 
                        {`${subtitleProps.trendIcon}${changeText}% ${subtitleProps.changeDescription}`}
                    </span>
                </div>
            );
        }
        // Fallback if change couldn't be calculated but there was *some* data
        return <div className={styles.subtitle}>{t("companyPage.experience_title_2_tenders_won", "vs last year")}</div>; 
    };

    const endYear = DateUtil.today().year();
    const startYear = endYear - 4;
    const last5YearsWinningBids = winningBids.filter(bid => {
        const dateToUse = bid.awardDate ?? bid.deadline;
        const bidMoment = dateToUse ? moment(dateToUse) : null;
        
        return bidMoment?.isValid() && bidMoment.year() >= startYear && bidMoment.year() <= endYear;
    });
    // Calculate total winning bids in the last 5 years
    const totalWinsLast5Years = last5YearsWinningBids.length;

    return (
        <div
            className={`
                ${styles["graph-card"]}
                ${styles["experience-card"]}
                ${styles["show-details"]} // Always show details view now
            `}
        >
            <div className={styles["heading-container"]}>
                 <span className={styles["graph-type-title"]}>{t("companyPage.experience_graph_type_tenders_won")}</span>
                 
                 <h3 className={styles["last-year-percentage"]}>{totalWinsLast5Years}</h3>
                 {/* {renderSubtitleContent()} */}
            </div>
            <div className={styles["graph-container"]}>
                {/* Pass winning bids to the trend graph (it calculates its own yearly data) */}
                <ExperienceTrendGraph bids={winningBids} />
            </div>
        </div>
    );
}

// Renamed from ExperienceDetailsGraph for clarity
interface ExperienceTrendGraphProps {
    bids: CompanyPageBid[]; // Expects already filtered (winning) bids
}
const ExperienceTrendGraph: React.FC<ExperienceTrendGraphProps> = (props) => {
    const { t } = useTranslation();
    const winningBids = props.bids; // Already filtered winning bids

    // Define the years range (e.g., last 5 years)
    // Corrected DateUtil usage
    const years = Array.from({length: 5}, (_, i) => DateUtil.today().subtract(4 - i, 'years').year());

    const getUniqueWinsCountForYear = (year: number): number => {
        const yearBids = winningBids.filter(bid => {
            // Use awardDate first, fallback to deadline
            const dateToUse = bid.awardDate ?? bid.deadline;
            const bidMoment = dateToUse ? moment(dateToUse) : null;
            return bidMoment?.isValid() && bidMoment.year() === year;
        });
        // Return the count of winning bids for the year
        return yearBids.length;
    };

    // Ensure DataStackedAttributes type matches BarGraphStacked expectation
    const data: DataStackedAttributes[] = years.map(year => ({
        x: year, // Use year number directly for x-axis
        y: [getUniqueWinsCountForYear(year)],
        label: [t('companyPage.graphLabel.tenders_won')], // Use a consistent label
    }));

    // Find max *unique win* count for Y-axis domain
    const maxUniqueWins = data.length > 0 ? Math.max(...data.map(d => d.y[0])) : 0;

    return (
        <BarGraphStacked
            data={data}
            showXAxis={true}
            showYAxis={true}
            // Dynamic Y-axis based on max unique wins
            yDomain={[0, Math.max(5, Math.ceil(maxUniqueWins * 1.1))]} 
            // Ensure x-axis treats values as years (categories) if needed, or format ticks
            // Depending on BarGraphStacked implementation, might need xformatter
        />
    );
} 